import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../lib/toast';
import Icon from '../../components/dashboard/DashboardIcons';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const REQ_STATUS_STYLES = {
  pending: 'bg-amber-50 text-amber-700 border border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border border-red-200',
};
const REQ_STATUS_LABELS = { pending: 'قيد المراجعة', approved: 'تم القبول', rejected: 'مرفوض' };

const AcademyCard = ({ academy, onOpen }) => (
  <div
    data-testid={`parent-academy-card-${academy.id}`}
    className="group relative overflow-hidden rounded-3xl border border-slate-200/80 bg-white p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
  >
    <div className="flex items-center gap-3">
      {academy.logo_file_id ? (
        <img src={`${API}/files/${academy.logo_file_id}`} alt="" className="h-14 w-14 rounded-xl border border-slate-100 object-contain" />
      ) : (
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-teal-50 text-lg font-black text-teal-600"><Icon name="building" className="h-7 w-7" /></div>
      )}
      <div className="min-w-0 flex-1">
        <div className="truncate text-base font-black text-slate-900">{academy.name}</div>
        {academy.city && <div className="text-xs font-semibold text-slate-500">{academy.city}</div>}
      </div>
    </div>
    {academy.sports.length > 0 && (
      <div className="mt-3 flex flex-wrap gap-1.5">
        {academy.sports.map((s) => <span key={s} className="rounded-full bg-teal-50 px-2.5 py-1 text-[11px] font-bold text-teal-700">{s}</span>)}
      </div>
    )}
    <div className="mt-3 text-[11px] font-semibold text-slate-400">
      {academy.programs_count > 0 ? `${academy.programs_count} برنامج متاح` : 'لا توجد برامج محددة — يمكنك إرسال طلب تسجيل عام'}
    </div>
    <button
      onClick={() => onOpen(academy)}
      data-testid={`parent-academy-open-${academy.id}`}
      className="mt-4 w-full rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white transition-colors hover:bg-teal-700 active:scale-95"
    >
      عرض التفاصيل والتسجيل
    </button>
  </div>
);

const ProgramRow = ({ program, onEnroll }) => {
  const full = program.remaining_slots <= 0;
  return (
    <div className="rounded-2xl border border-slate-100 bg-slate-50 p-3" data-testid={`parent-program-card-${program.id}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-bold text-slate-900">{program.name}</div>
        <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${full ? 'bg-red-50 text-red-600' : 'bg-teal-50 text-teal-700'}`}>
          {full ? 'مكتمل' : `${program.remaining_slots} مقعد`}
        </span>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] font-bold text-slate-500">
        <span>{program.sport}</span>
        {(program.min_age || program.max_age) && <span>· الأعمار {program.min_age || 0}–{program.max_age || '+'}</span>}
        <span>· {program.price ? `${program.price} ${program.currency || 'SAR'} / ${program.billing_period}` : 'مجاني'}</span>
      </div>
      <button
        onClick={() => onEnroll(program)}
        disabled={full}
        data-testid={`parent-program-open-${program.id}`}
        className="mt-2 w-full rounded-lg bg-teal-600 py-2 text-xs font-bold text-white disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {full ? 'لا تتوفر مقاعد' : 'التسجيل في هذا البرنامج'}
      </button>
    </div>
  );
};

const EnrollForm = ({ onSubmit, submitting }) => {
  const [childName, setChildName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [notes, setNotes] = useState('');
  return (
    <div className="space-y-3">
      <div>
        <label className="mb-1 block text-xs font-bold text-slate-600">اسم الابن الكامل *</label>
        <input value={childName} onChange={(e) => setChildName(e.target.value)} data-testid="parent-enroll-child-name-input" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none" placeholder="اسم الابن" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-bold text-slate-600">تاريخ الميلاد</label>
        <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} data-testid="parent-enroll-birthdate-input" className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none" />
      </div>
      <div>
        <label className="mb-1 block text-xs font-bold text-slate-600">ملاحظات (اختياري)</label>
        <textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="parent-enroll-notes-input" rows={3} className="w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:border-teal-500 focus:outline-none" placeholder="أي معلومة تريد إضافتها للأكاديمية" />
      </div>
      <button
        onClick={() => onSubmit({ childName, birthDate, notes })}
        disabled={submitting}
        data-testid="parent-enroll-submit-btn"
        className="w-full rounded-xl bg-teal-600 py-2.5 text-sm font-bold text-white disabled:opacity-60"
      >
        {submitting ? 'جاري الإرسال...' : 'إرسال طلب التسجيل'}
      </button>
    </div>
  );
};

const AcademyDetailModal = ({ academyId, onClose, onSuccess }) => {
  const [academy, setAcademy] = useState(null);
  const [mode, setMode] = useState('view'); // view | enroll-general | enroll-program
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/parent/directory/academies/${academyId}`, auth()).then((res) => setAcademy(res.data)).catch(() => setAcademy(false));
  }, [academyId]);

  const submit = async ({ childName, birthDate, notes }) => {
    if (childName.trim().length < 2) { toast.error('اسم الابن مطلوب'); return; }
    setSubmitting(true);
    try {
      if (mode === 'enroll-program' && selectedProgram) {
        await axios.post(`${API}/parent/directory/enroll`, {
          program_id: selectedProgram.id, child_full_name: childName.trim(), child_birth_date: birthDate || null, notes: notes || null,
        }, auth());
      } else {
        await axios.post(`${API}/parent/directory/academies/${academyId}/enroll`, {
          child_full_name: childName.trim(), child_birth_date: birthDate || null, notes: notes || null,
        }, auth());
      }
      toast.success('تم إرسال طلب التسجيل للأكاديمية بنجاح');
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'فشل إرسال الطلب');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="parent-academy-detail-modal">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6">
        {academy === null ? (
          <div className="space-y-3">{[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-xl bg-slate-100" />)}</div>
        ) : academy === false ? (
          <p className="py-6 text-center text-sm text-slate-500">تعذر تحميل بيانات الأكاديمية</p>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {academy.logo_file_id ? (
                  <img src={`${API}/files/${academy.logo_file_id}`} alt="" className="h-12 w-12 rounded-xl border border-slate-100 object-contain" />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-teal-50 text-teal-600"><Icon name="building" className="h-6 w-6" /></div>
                )}
                <div>
                  <h4 className="text-lg font-black text-slate-900">{academy.name}</h4>
                  {academy.city && <p className="text-xs text-slate-500">{academy.city}</p>}
                </div>
              </div>
              <button onClick={onClose} data-testid="parent-academy-modal-close"><Icon name="x" className="h-5 w-5 text-slate-400" /></button>
            </div>
            {academy.description && <p className="mt-3 text-sm text-slate-600">{academy.description}</p>}

            {mode === 'view' && (
              <div className="mt-5 space-y-2">
                <h5 className="text-sm font-bold text-slate-700">البرامج المتاحة</h5>
                {academy.programs.length === 0 ? (
                  <p className="text-xs text-slate-400">لا توجد برامج محددة لدى هذه الأكاديمية حالياً</p>
                ) : (
                  <div className="space-y-2" data-testid="parent-academy-programs-list">
                    {academy.programs.map((p) => (
                      <ProgramRow key={p.id} program={p} onEnroll={(prog) => { setSelectedProgram(prog); setMode('enroll-program'); }} />
                    ))}
                  </div>
                )}
                <button
                  onClick={() => { setSelectedProgram(null); setMode('enroll-general'); }}
                  data-testid="parent-academy-general-enroll-btn"
                  className="mt-3 w-full rounded-xl border-2 border-dashed border-teal-300 py-2.5 text-sm font-bold text-teal-700 hover:bg-teal-50"
                >
                  طلب تسجيل عام (بلا برنامج محدد)
                </button>
              </div>
            )}

            {(mode === 'enroll-program' || mode === 'enroll-general') && (
              <div className="mt-5">
                <button onClick={() => setMode('view')} className="mb-3 flex items-center gap-1 text-xs font-bold text-slate-500 hover:text-teal-600" data-testid="parent-academy-back-btn">
                  <Icon name="arrowLeft" className="h-3.5 w-3.5" /> رجوع
                </button>
                <h5 className="mb-3 text-sm font-bold text-slate-700">
                  {mode === 'enroll-program' ? `التسجيل في «${selectedProgram?.name}»` : 'طلب تسجيل عام'}
                </h5>
                <EnrollForm onSubmit={submit} submitting={submitting} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default function ParentExplorePage() {
  const [tab, setTab] = useState('browse');
  const [filters, setFilters] = useState({ cities: [], sports: [] });
  const [city, setCity] = useState('');
  const [sport, setSport] = useState('');
  const [academies, setAcademies] = useState(null);
  const [openAcademyId, setOpenAcademyId] = useState(null);
  const [requests, setRequests] = useState(null);

  useEffect(() => {
    axios.get(`${API}/parent/directory/filters`, auth()).then((res) => setFilters(res.data || { cities: [], sports: [] })).catch(() => {});
  }, []);

  const loadAcademies = useCallback(async () => {
    setAcademies(null);
    try {
      const params = {};
      if (city) params.city = city;
      if (sport) params.sport = sport;
      const res = await axios.get(`${API}/parent/directory/academies`, { ...auth(), params });
      setAcademies(res.data || []);
    } catch { setAcademies([]); }
  }, [city, sport]);

  const loadRequests = useCallback(async () => {
    setRequests(null);
    try {
      const res = await axios.get(`${API}/parent/me/enrollment-requests`, auth());
      setRequests(res.data || []);
    } catch { setRequests([]); }
  }, []);

  useEffect(() => { if (tab === 'browse') loadAcademies(); }, [tab, loadAcademies]);
  useEffect(() => { if (tab === 'requests') loadRequests(); }, [tab, loadRequests]);

  return (
    <div className="space-y-5" data-testid="parent-explore-page">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-black text-[#0A192F]">استكشاف الأكاديميات والبرامج</h1>
      </div>

      <div className="flex gap-1 rounded-xl bg-slate-100 p-1" data-testid="parent-explore-tabs">
        <button onClick={() => setTab('browse')} data-testid="parent-explore-tab-browse" className={`flex-1 rounded-lg py-2 text-sm font-bold transition-all ${tab === 'browse' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}>الأكاديميات</button>
        <button onClick={() => setTab('requests')} data-testid="parent-explore-tab-requests" className={`flex-1 rounded-lg py-2 text-sm font-bold transition-all ${tab === 'requests' ? 'bg-white text-teal-700 shadow-sm' : 'text-slate-500'}`}>طلباتي</button>
      </div>

      {tab === 'browse' && (
        <>
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 bg-white p-3" data-testid="parent-explore-filters">
            <select value={city} onChange={(e) => setCity(e.target.value)} data-testid="parent-explore-filter-city" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-700 focus:border-teal-500 focus:outline-none">
              <option value="">كل المدن</option>
              {filters.cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select value={sport} onChange={(e) => setSport(e.target.value)} data-testid="parent-explore-filter-sport" className="rounded-lg border border-slate-200 px-2.5 py-2 text-xs font-semibold text-slate-700 focus:border-teal-500 focus:outline-none">
              <option value="">كل الرياضات</option>
              {filters.sports.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          {academies === null ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{[1, 2, 3, 4].map((i) => <div key={i} className="h-40 animate-pulse rounded-3xl bg-slate-100" />)}</div>
          ) : academies.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="parent-explore-empty">
              <Icon name="inbox" className="h-10 w-10 text-slate-300" />
              <p className="mt-3 text-sm font-bold text-slate-700">لا توجد أكاديميات مطابقة لهذه الفلاتر حالياً</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="parent-explore-academies-grid">
              {academies.map((a) => <AcademyCard key={a.id} academy={a} onOpen={(ac) => setOpenAcademyId(ac.id)} />)}
            </div>
          )}
        </>
      )}

      {tab === 'requests' && (
        requests === null ? (
          <div className="space-y-2">{[1, 2].map((i) => <div key={i} className="h-16 animate-pulse rounded-2xl bg-slate-100" />)}</div>
        ) : requests.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center" data-testid="parent-requests-empty">
            <Icon name="clipboard" className="h-10 w-10 text-slate-300" />
            <p className="mt-3 text-sm font-bold text-slate-700">لم تُرسل أي طلبات تسجيل بعد</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="parent-enrollment-requests-list">
            {requests.map((r) => (
              <div key={r.id} className="rounded-2xl border border-slate-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-bold text-slate-900">{r.child_full_name}</div>
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold ${REQ_STATUS_STYLES[r.status]}`}>{REQ_STATUS_LABELS[r.status]}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{r.program_name} · {r.club_name}</div>
                {r.status === 'rejected' && r.rejection_reason && (
                  <div className="mt-2 rounded-lg bg-red-50 px-3 py-1.5 text-[11px] text-red-600">سبب الرفض: {r.rejection_reason}</div>
                )}
              </div>
            ))}
          </div>
        )
      )}

      {openAcademyId && (
        <AcademyDetailModal
          academyId={openAcademyId}
          onClose={() => setOpenAcademyId(null)}
          onSuccess={() => { setOpenAcademyId(null); setTab('requests'); loadRequests(); }}
        />
      )}
    </div>
  );
}
